// packages/chatbot/src/utils/messages.ts

import type { Message } from '@dopomogai/types';
import type { UIMessage } from '../types';

/**
 * Converts backend Message[] -> UIMessage[] for rendering in the transcript.
 * Extended: includes 'context-info', 'system-instruction', 'tool-return'.
 */
export function processBackendMessagesToUI(messages: Message[]): UIMessage[] {
  const processed: UIMessage[] = [];
  if (!Array.isArray(messages)) return [];

  messages.forEach((message, index) => {
    const baseKey = (message as any).id || `${message.timestamp || ''}-${index}`;

    if (message.kind === 'request' && message.parts) {
      const userPromptPart = message.parts.find((p) => p.part_kind === 'user-prompt');
      if (userPromptPart) {
        processed.push({
          id: `hist-${baseKey}-user`,
          type: 'user',
          content: userPromptPart.content as string,
          timestamp: message.timestamp,
        });
      }
    } else if (message.kind === 'response' && message.parts) {
      message.parts.forEach((part, partIndex) => {
        const partKey = `${baseKey}-${part.part_kind}-${partIndex}`;

        if (part.part_kind === 'llm-response' || part.part_kind === 'text') {
          processed.push({
            id: partKey,
            type: 'assistant',
            content: part.content as string,
            model: message.model_name || undefined,
            timestamp: message.timestamp,
            isStreaming: false,
          });
        } else if (part.part_kind === 'tool-call') {
          const toolCallId = (part as any).tool_call_id || `tool-call-${partKey}`;
          let parsedArgs: Record<string, any> = {};
          try {
            const args = (part as any).args;
            if (args && typeof args === 'string') parsedArgs = JSON.parse(args);
            else if (args && typeof args === 'object') parsedArgs = args;
          } catch {
            parsedArgs = { _error: 'Failed to parse arguments', _raw: (part as any).args };
          }
          processed.push({
            id: toolCallId,
            type: 'tool_call_start',
            tool_call_id: toolCallId,
            tool_name: (part as any).tool_name || 'unknown_tool',
            arguments: parsedArgs,
            timestamp: message.timestamp,
          });
        } else if (
          part.part_kind === 'tool-response' ||
          part.part_kind === 'tool-result' ||
          part.part_kind === 'tool-return'
        ) {
          const toolCallId = (part as any).tool_call_id || `tool-call-result-${partKey}`;
          processed.push({
            id: `resp-for-${toolCallId}`,
            type: 'tool_response_end',
            tool_call_id: toolCallId,
            tool_name: (part as any).tool_name || 'unknown_tool',
            output: (part as any).output ?? (part as any).content ?? 'Tool executed.',
            timestamp: message.timestamp,
          });
        } else if (part.part_kind === 'system-instruction') {
          const raw = (part as any).content;
          const contentStr = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
          processed.push({
            id: partKey,
            type: 'system_log',
            content: contentStr,
            severity: 'info',
            timestamp: message.timestamp,
          } as any);
        } else if (part.part_kind === 'context-info') {
          const raw = (part as any).content;
          const contentStr = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
          processed.push({
            id: partKey,
            type: 'system_log',
            content: contentStr,
            severity: 'info',
            timestamp: message.timestamp,
          } as any);
        } else if (part.part_kind === 'error') {
          const raw = (part as any).content || (part as any).message || (part as any);
          const contentStr = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
          processed.push({
            id: partKey,
            type: 'system_log',
            content: contentStr,
            severity: 'error',
            timestamp: message.timestamp,
          } as any);
        }
      });
    }
  });

  return processed;
}
